import { Component } from '@angular/core'
import { IFilterAngularComp } from '@ag-grid-community/angular'
import { IDoesFilterPassParams, IFilterParams } from '@ag-grid-community/core'
import {
  COLUMN_TYPE_OVERRIDE_OPERATOR_LABELS,
  COLUMN_TYPE_OVERRIDE_OPERATORS,
  ColumnTypeOverride,
  ColumnTypeOverrideService,
  DEFAULT_COLUMN_TYPE_OVERRIDE,
} from '@helios/filter-modal'

/**
 * The `frameworkComponents` key {@link AggInfoColumnFilterComponent} is registered under.
 *
 * ag-Grid 26 types `ColDef.filter` as `new () => IFilterComp`, which an ANGULAR filter component can
 * never satisfy — `getGui` is supplied by ag-Grid's own Angular wrapper, not by the component class.
 * So the filter is referenced BY NAME and registered in the grid's `frameworkComponents`, which is the
 * same idiom the Northfork filters in `@helios/ag-grid-config` use.
 */
export const AGG_INFO_FILTER = 'aggInfoColumnFilter'

/** The `filterParams` an AGG_INFO column's ColDef must supply for this filter to work. */
export interface AggInfoColumnFilterParams extends IFilterParams {
  columnTypeOverride: { scope: string; type: ColumnTypeOverride }
}

/**
 * The operators offered per chosen type, and their labels.
 *
 * Both live in `@helios/filter-modal` rather than here because the FILTER MODAL offers the same
 * operators for the same column, and two copies would drift — a predicate built in one surface would
 * become unreachable in the other. See `COLUMN_TYPE_OVERRIDE_OPERATORS` for why the vocabulary is
 * ag-Grid's own: {@link getModel} emits ag-Grid's simple-filter model shape, which is what
 * `@helios/server-side-util`'s `convertFilters` knows how to translate into the /query contract.
 * Inventing an operator would produce a model the converter drops on the floor, silently
 * un-filtering the column.
 */
const OPERATORS = COLUMN_TYPE_OVERRIDE_OPERATORS
const OPERATOR_LABELS = COLUMN_TYPE_OVERRIDE_OPERATOR_LABELS

/** Operators that carry no value at all, so an empty input still means an active filter. */
const VALUELESS = new Set(['blank', 'notBlank'])

/** Operators taking a second value. */
const RANGE = new Set(['inRange'])

/**
 * The grid-header filter for an AGGREGATE_INFO column, replacing `agSetColumnFilter`.
 *
 * Two reasons the set filter cannot stay:
 *
 *  1. **It has no values to show.** On the server-side row model a set filter has to be handed its
 *     values explicitly; the only other source is the rows currently loaded, and deriving them from
 *     those would present the first block's distinct values as though they were the column's whole
 *     domain — a filter that silently lies about what exists beyond the loaded rows. An agg-info key
 *     is an arbitrary open-domain JSON value, so there is no closed list to supply either.
 *  2. **The user owns this column's comparison type**, and a set filter has nowhere to say so. Every
 *     AGGREGATE_INFO column's catalog type is a `TEXT` placeholder, so the type has to be a control
 *     rather than a fact — and the choice has to reach the request as `filterType` / sort `type` /
 *     the field's presence under `context.fieldStorage.aggInfo`.
 *
 * So this hosts the SAME `hs-column-type-picker` the filter modal's editor row does, writing the same
 * {@link ColumnTypeOverrideService}, above a typed condition for the chosen type. Both surfaces
 * therefore agree by construction rather than by convention.
 *
 * `doesFilterPass` is a documented no-op: filtering happens server-side, and ag-Grid never consults
 * it on the server-side row model. It is implemented (returning `true`) rather than thrown from, so
 * this same component keeps working if a client-side grid ever hosts it.
 */
@Component({
  selector: 'hs-agg-info-column-filter',
  templateUrl: './agg-info-column-filter.component.html',
  styleUrls: ['./agg-info-column-filter.component.scss'],
})
export class AggInfoColumnFilterComponent implements IFilterAngularComp {
  type: ColumnTypeOverride = DEFAULT_COLUMN_TYPE_OVERRIDE
  operator = 'contains'
  value = ''
  valueTo = ''

  private params!: AggInfoColumnFilterParams
  private scope = ''
  private field = ''

  /**
   * No constructor parameters, deliberately: ag-Grid's `ColDef.filter` is typed `new () => IFilterComp`,
   * so a component declaring constructor DI would not type-check as a filter component at all. The
   * override store is read off `filterParams` instead, which the ColDef factory populates from the
   * panel's own injector — and which is the same place the chosen type already travels.
   */
  private get overrides(): ColumnTypeOverrideService | undefined {
    return this.params?.colDef?.filterParams?.columnTypeOverrideService
  }

  agInit(params: AggInfoColumnFilterParams): void {
    this.params = params
    this.scope = params.columnTypeOverride?.scope ?? ''
    this.field = params.colDef?.colId ?? params.colDef?.field ?? ''
    this.type = params.columnTypeOverride?.type ?? DEFAULT_COLUMN_TYPE_OVERRIDE
    this.operator = this.operators[0]
  }

  get operators(): readonly string[] {
    return OPERATORS[this.type]
  }

  labelFor(operator: string): string {
    return OPERATOR_LABELS[operator] ?? operator
  }

  get isRange(): boolean {
    return RANGE.has(this.operator)
  }

  get needsValue(): boolean {
    return !VALUELESS.has(this.operator)
  }

  /**
   * Changing the type discards the condition. `contains "12/31"` is not a meaningful date predicate,
   * and carrying it across is one of the ways a retry after a 400 INVALID_FIELD_TYPE fails
   * identically — see the reset-to-Text path.
   *
   * Deliberately does NOT write to {@link ColumnTypeOverrideService} or call
   * `filterChangedCallback()` here — picking a type is an in-progress edit, same as picking an
   * operator or typing a value, and only Apply commits it (see {@link apply}). Writing immediately
   * would rebuild the grid's ColDefs — and reset whatever the user has since done in the Column
   * Manager — before Apply is even clicked, or even if the popover is dismissed without applying.
   */
  onTypeChange(type: ColumnTypeOverride): void {
    this.type = type
    this.operator = this.operators[0]
    this.value = ''
    this.valueTo = ''
  }

  onOperatorChange(operator: string): void {
    this.operator = operator
    if (!this.needsValue) {
      this.value = ''
      this.valueTo = ''
    }
  }

  apply(): void {
    if (this.scope && this.field) {
      // `true`: this call IS that field's own filter instance, mid-apply — the store must not tell a
      // listener to destroy it, or the condition just typed below is blanked before
      // `filterChangedCallback()` gets a chance to read it back out via `getModel()`.
      this.overrides?.setType(this.scope, this.field, this.type, true)
    }
    this.params.filterChangedCallback()
  }

  clear(): void {
    // Discards the pending type pick too, back to whatever is currently committed — Reset means
    // "undo my in-progress edits", not "keep the type I was trying out".
    this.type =
      this.params.columnTypeOverride?.type ?? DEFAULT_COLUMN_TYPE_OVERRIDE
    this.operator = this.operators[0]
    this.value = ''
    this.valueTo = ''
    this.params.filterChangedCallback()
  }

  // ---- IFilterAngularComp ---------------------------------------------------

  isFilterActive(): boolean {
    if (!this.needsValue) return true
    if (this.value.trim() === '') return false
    return !this.isRange || this.valueTo.trim() !== ''
  }

  /**
   * Filtering is server-side and ag-Grid does not consult this on the server-side row model. Returning
   * `true` — rather than throwing — keeps the component usable if a client-side grid ever hosts it,
   * at the cost of being a no-op there; a client-side host would need a real predicate.
   */
  doesFilterPass(_params: IDoesFilterPassParams): boolean {
    return true
  }

  /**
   * The filter model for the chosen type, in the shape `@helios/server-side-util`'s `convertFilters`
   * actually reads. `null` when inactive, which is how ag-Grid drops the column from its filter model.
   *
   * ALL THREE branches carry the value on `filter` / `filterTo`, including the date one. That is not
   * ag-Grid's own native date-filter shape (`dateFrom` / `dateTo`) — the house converters read
   * Northfork's `ScalerFilterModel` instead, so a native-shaped date model converts to `null` and
   * `convertFilters` DROPS the entry: the predicate never reaches /query, the grid shows unfiltered
   * rows, and it is indistinguishable from "no filter applied". `filterType` still says `'date'`,
   * which is what routes the entry to the date converter and what the backend reads.
   */
  getModel(): unknown {
    if (!this.isFilterActive()) return null
    if (this.type === 'date') {
      return {
        filterType: 'date',
        type: this.operator,
        filter: this.value || null,
        filterTo: this.isRange ? this.valueTo || null : null,
      }
    }
    if (this.type === 'number') {
      return {
        filterType: 'number',
        type: this.operator,
        filter: this.value === '' ? null : Number(this.value),
        filterTo:
          this.isRange && this.valueTo !== '' ? Number(this.valueTo) : null,
      }
    }
    return {
      filterType: 'text',
      type: this.operator,
      filter: this.value,
    }
  }

  setModel(model: unknown): void {
    const record = (model ?? {}) as Record<string, unknown>
    if (!model) {
      this.operator = this.operators[0]
      this.value = ''
      this.valueTo = ''
      return
    }
    this.operator = String(record['type'] ?? this.operators[0])
    // `dateFrom` / `dateTo` are read as a fallback so a model persisted by an ag-Grid-native date
    // filter (or a saved view written before getModel emitted `filter`) still restores.
    const from = record['filter'] ?? record['dateFrom']
    const to = record['filterTo'] ?? record['dateTo']
    this.value = from == null ? '' : String(from)
    this.valueTo = to == null ? '' : String(to)
  }
}
